import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TableSortLabel,
  LinearProgress,
  ThemeProvider,
  createTheme
} from '@mui/material';
import { pageColors } from '../pages/HomePage';
import apiFetch from '../api/apiFetch';

export default function InventoryPage({ connected, setConnected, setError }) {
  const [inventory, setInventory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [order, setOrder] = useState('asc');
  const [orderBy, setOrderBy] = useState('itemId');

  const handleSearch = async () => {
    if (!connected) {
      setError('Not connected to Fortnox. Please connect first.');
      setConnected(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/api/inventory');
      if (!response.ok) {
        const errorText = await response.text();
        let errorMessage;
        try {
          const errorJson = JSON.parse(errorText);
          errorMessage = errorJson.error || 'Unknown error';
        } catch (e) {
          errorMessage = errorText || `HTTP ${response.status}`;
        }
        if (response.status === 401) {
          setConnected(false);
          setError('Lost connection to Fortnox: Unauthorized. Please reconnect.');
        } else {
          setError(`Failed to fetch inventory: ${errorMessage}. Please try again.`);
        }
        return;
      }
      const data = await response.json();
      setInventory(data);
      setConnected(true);
    } catch (err) {
      setConnected(false);
      setError('Unable to reach the server. Please check your connection or reconnect to Fortnox.');
    } finally {
      setLoading(false);
    }
  };

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const comparator = (a, b) => {
    let valA = a[orderBy] ?? '';
    let valB = b[orderBy] ?? '';
    if (typeof valA === 'number' && typeof valB === 'number') {
      return order === 'asc' ? valA - valB : valB - valA;
    } else {
      return order === 'asc'
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    }
  };

  const sortedInventory = [...inventory].sort(comparator);

  // Create a theme using the Inventory color
  const inventoryTheme = createTheme({
    palette: {
      primary: {
        main: pageColors.inventory,
      },
    },
  });

  return (
    <ThemeProvider theme={inventoryTheme}>
      <Box
        sx={{
          backgroundColor: `${pageColors.inventory}15`,
          minHeight: '100vh',
          pt: { xs: 3, md: 4 },
          pb: { xs: 6, md: 8 },
          px: { xs: 1.5, sm: 3 },
        }}
      >
        <Container maxWidth="lg">
          <Typography
            variant="h4"
            sx={{
              color: pageColors.inventory,
              fontWeight: 'bold',
              mb: 3,
            }}
          >
            Inventory Levels
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={handleSearch}
              disabled={loading}
              sx={{ alignSelf: 'center' }}
            >
              Search
            </Button>
          </Box>
          {loading && (
            <Box sx={{ width: '100%', mb: 2 }}>
              <LinearProgress />
            </Box>
          )}
          <TableContainer component={Paper} variant="outlined">
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'itemId'}
                      direction={orderBy === 'itemId' ? order : 'asc'}
                      onClick={() => handleRequestSort('itemId')}
                    >
                      <strong>Item ID</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'stockPointCode'}
                      direction={orderBy === 'stockPointCode' ? order : 'asc'}
                      onClick={() => handleRequestSort('stockPointCode')}
                    >
                      <strong>Stock Point Code</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'inStock'}
                      direction={orderBy === 'inStock' ? order : 'asc'}
                      onClick={() => handleRequestSort('inStock')}
                    >
                      <strong>In Stock</strong>
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>
                    <TableSortLabel
                      active={orderBy === 'availableStock'}
                      direction={orderBy === 'availableStock' ? order : 'asc'}
                      onClick={() => handleRequestSort('availableStock')}
                    >
                      <strong>Available Stock</strong>
                    </TableSortLabel>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedInventory.map((item, index) => (
                  <TableRow key={index}>
                    <TableCell>{item.itemId}</TableCell>
                    <TableCell>{item.stockPointCode}</TableCell>
                    <TableCell>{item.inStock}</TableCell>
                    <TableCell>{item.availableStock}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
